import type { BlockMedicalConditions } from 'types/block-medical-conditions';
import type { Condition } from 'types/condition';

import { memo, useReducer } from 'react';
import { Paragraph, Wrap } from '@leafwell/components';
import Image from 'next/image';

import LinkArrow from './link-arrow';
import Pagination from './pagination-clientside';
import WysiwygContent from './wysiwyg-content';

type PaginatedConditions = {
  items: Condition[];
  offset: number;
};

type ConditionsRelatedState = PaginatedConditions;

export type ConditionsRelatedAction =
  | {
      type: 'updateHeight';
      value: number;
    }
  | {
      type: 'paginate';
      value: PaginatedConditions;
    };

const CONDITIONS_PER_PAGE = 10;
const SECTION_HEIGHT = CONDITIONS_PER_PAGE * 40;

const paginate = (items: Condition[], offset = 0): PaginatedConditions => {
  return { offset, items: items.slice(offset, offset + CONDITIONS_PER_PAGE) };
};

export const conditionsRelatedReducer = (
  state: ConditionsRelatedState,
  action: ConditionsRelatedAction,
): ConditionsRelatedState => {
  switch (action.type) {
    case 'paginate': {
      return {
        ...state,
        ...paginate(action.value.items, action.value.offset),
      };
    }
  }
};

const ConditionsRelated: React.FC<BlockMedicalConditions> = memo(
  ({ conditions, description, extraInfo, title, showIcon, sectionId }) => {
    const [{ items, offset }, updateSettings] = useReducer(
      conditionsRelatedReducer,
      { ...paginate(conditions) },
    );

    return (
      <Wrap
        id={sectionId}
        className="md:grid-cols-2 lg:grid-cols-conditions-related md:gap-x-16 gap-y-4"
      >
        {showIcon && (
          <Image
            src="/asterisk.svg"
            alt=""
            className="w-8 col-span-full"
            loading="lazy"
            width={32}
            height={32}
          />
        )}

        <div className="grid gap-6">
          <header className="grid gap-6 content-start">
            <Paragraph
              size="large"
              tagName="h2"
              className={
                showIcon
                  ? 'font-medium'
                  : '!text-3xl sm:!text-5xl leading-tight'
              }
              dangerouslySetInnerHTML={{ __html: title }}
            />
            {description && (
              <Paragraph
                size="large"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </header>
          {extraInfo?.length > 0 && (
            <footer className="self-end lg:max-w-xs">
              <Image
                src="/info.svg"
                alt=""
                loading="lazy"
                width={20}
                height={20}
              />
              <WysiwygContent content={extraInfo} />
            </footer>
          )}
        </div>
        <section className="grid" style={{ minHeight: SECTION_HEIGHT }}>
          <ul>
            {items.map(({ id, title, link }) => (
              <li className="animate-fade-in" key={id}>
                <LinkArrow
                  {...{
                    title,
                    uri: link,
                  }}
                />
              </li>
            ))}
          </ul>
          {conditions.length > CONDITIONS_PER_PAGE && (
            <Pagination
              {...{
                className: 'self-end mt-6',
                offset,
                limit: CONDITIONS_PER_PAGE,
                onPageChange: offset =>
                  updateSettings({
                    type: 'paginate',
                    value: { items: conditions, offset },
                  }),
                total: conditions.length,
                variant: 'minimal',
              }}
            />
          )}
        </section>
      </Wrap>
    );
  },
);

export default ConditionsRelated;
